import React from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';

const Badge = React.forwardRef(({
  className,
  variant = 'default',
  size = 'default',
  icon = null,
  iconPosition = 'left',
  dot = false,
  children,
  ...props
}, ref) => {
  const variantClasses = {
    default: 'bg-neutral-100 text-neutral-800 border-neutral-200',
    primary: 'bg-primary-100 text-primary-800 border-primary-200',
    secondary: 'bg-secondary-100 text-secondary-800 border-secondary-200',
    success: 'bg-success-100 text-success-800 border-success-200',
    warning: 'bg-warning-100 text-warning-800 border-warning-200',
    danger: 'bg-danger-100 text-danger-800 border-danger-200',
    accent: 'bg-accent-100 text-accent-800 border-accent-200',
    // Solid variants
    'primary-solid': 'bg-primary-500 text-white border-primary-600',
    'secondary-solid': 'bg-secondary-500 text-white border-secondary-600',
    'success-solid': 'bg-success-500 text-white border-success-600',
    'warning-solid': 'bg-warning-500 text-warning-900 border-warning-600',
    'danger-solid': 'bg-danger-500 text-white border-danger-600',
    'accent-solid': 'bg-accent-500 text-white border-accent-600',
    // Outline variants
    'primary-outline': 'bg-transparent text-primary-700 border-primary-300',
    'secondary-outline': 'bg-transparent text-secondary-700 border-secondary-300',
    'success-outline': 'bg-transparent text-success-700 border-success-300',
    'warning-outline': 'bg-transparent text-warning-700 border-warning-300',
    'danger-outline': 'bg-transparent text-danger-700 border-danger-300',
    'accent-outline': 'bg-transparent text-accent-700 border-accent-300'
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-caption-sm',
    default: 'px-2.5 py-1 text-caption',
    lg: 'px-3 py-1.5 text-caption-lg',
    xl: 'px-4 py-2 text-body-sm'
  };

  const iconSizeMap = {
    sm: 10,
    default: 12,
    lg: 14,
    xl: 16
  };

  const renderIcon = () => {
    if (!icon) return null;
    return (
      <Icon
        name={icon}
        size={iconSizeMap?.[size]}
        className={cn(
          children && iconPosition === 'left' && 'mr-1',
          children && iconPosition === 'right' && 'ml-1'
        )}
      />
    );
  };

  const renderDot = () => {
    if (!dot) return null;
    return (
      <span className={cn(
        'w-1.5 h-1.5 rounded-full',
        children && 'mr-1.5',
        variant?.includes('solid') ? 'bg-white' : 'bg-current'
      )} />
    );
  };

  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-md border transition-smooth',
        variantClasses?.[variant],
        sizeClasses?.[size],
        className
      )}
      {...props}
    >
      {dot && renderDot()}
      {icon && iconPosition === 'left' && renderIcon()}
      {children}
      {icon && iconPosition === 'right' && renderIcon()}
    </span>
  );
});

Badge.displayName = 'Badge';

export default Badge;